import React, { useEffect, useRef, useState } from 'react'
import { Bot, PhoneCall, Boxes, ShoppingBag, Radar, BrainCircuit } from 'lucide-react'
import Logo from './Logo'
import './auth-constellation.css'

/**
 * AuthConstellation — "Constellation of Sources".
 *
 * The left brand panel as a dark, cinematic star-chart. A field of ~50 dim
 * monochrome points (your scattered sources) drifts almost imperceptibly around
 * a still, bright `R | Я` anchor mark. One at a time, six loose clusters resolve
 * into a service: the cluster points brighten → thin lines draw between them and
 * up to the anchor → a small icon + one short outcome line fade in → the red
 * highlight settles to ink and hands off to the next. After all six connect, the
 * sky holds calm, fades to dim, and rebuilds in a different order. Loops.
 *
 * Monochrome throughout; red is reserved for the anchor's glow and the single
 * constellation currently forming. Slow + subtle + `prefers-reduced-motion` safe
 * (reduced → the full map, fully drawn and still).
 *
 * Self-contained: renders a `.rrcon` wrapper; all CSS is scoped under `.rrcon`.
 */

const VB_W = 600
const VB_H = 860
const ANCHOR = { x: 300, y: 170 }

// Six hand-placed constellations (art-directed, not random) — two columns,
// three rows flowing down the tall panel; the anchor sits above at upper-centre.
// `edges` connect points into a legible asterism; the hub (nearest the anchor)
// carries the line up to the mark; the badge is centred beneath the cluster.
const RAW = [
  { name: 'Support Chatbot', best: 'Every visitor gets an instant answer', Icon: Bot,
    pts: [[110, 306], [148, 262], [196, 292], [176, 346], [120, 340]],
    edges: [[0, 1], [1, 2], [2, 3], [3, 4]] },

  { name: 'Voice AI Receptionist', best: 'No call ever goes to voicemail', Icon: PhoneCall,
    pts: [[440, 258], [488, 300], [446, 350], [402, 300]],
    edges: [[0, 1], [1, 2], [2, 3], [3, 0]] },

  { name: 'Custom RAG API', best: 'Answers cited to your own docs', Icon: Boxes,
    pts: [[118, 470], [206, 468], [208, 536], [120, 538]],
    edges: [[0, 1], [1, 2], [2, 3], [3, 0]] },

  { name: 'WhatsApp Commerce', best: 'Shoppers check out inside the chat', Icon: ShoppingBag,
    pts: [[396, 500], [432, 460], [486, 478], [492, 538], [418, 550]],
    edges: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 0]] },

  { name: 'SEO Intelligence', best: 'Cited by ChatGPT & Perplexity', Icon: Radar,
    pts: [[120, 700], [202, 674], [176, 744]],
    edges: [[0, 1], [1, 2], [2, 0]] },

  { name: 'Marketing Brain', best: 'You see why revenue moved', Icon: BrainCircuit,
    pts: [[388, 700], [430, 660], [480, 684], [492, 734], [428, 748]],
    edges: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 0]] },
]

const dist = (p, q) => Math.hypot(p.x - q.x, p.y - q.y)

// Resolve every constellation once, defensively, so render can never crash.
const MODEL = RAW.map((c) => {
  const pts = (c.pts || []).map(([x, y]) => ({ x, y })).filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y))
  const edges = (c.edges || []).filter(([a, b]) => pts[a] && pts[b])
  let hub = 0, hd = Infinity
  pts.forEach((p, i) => { const d = dist(p, ANCHOR); if (d < hd) { hd = d; hub = i } })
  const cx = pts.length ? pts.reduce((s, p) => s + p.x, 0) / pts.length : VB_W / 2
  const maxY = pts.length ? pts.reduce((m, p) => Math.max(m, p.y), 0) : 0
  const nAst = edges.length
  return {
    name: c.name, best: c.best, Icon: c.Icon,
    pts, edges, hub, nAst,
    badge: { x: cx - 105, y: maxY + 14, w: 210, h: 70 },
    // stagger (seconds): brighten → asterism lines → anchor line → badge
    anchorDelay: 0.5 + nAst * 0.28 + 0.1,
    badgeDelay: 0.5 + nAst * 0.28 + 0.5,
  }
})

// Deterministic star-field so the layout is stable and reads as intentional.
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const STARS = (() => {
  const rnd = mulberry32(20260827)
  const out = []
  let guard = 0
  while (out.length < 52 && guard < 4000) {
    guard++
    const x = 26 + rnd() * (VB_W - 52)
    const y = 26 + rnd() * (VB_H - 52)
    // keep the anchor mark clear of stray points
    if (x > 205 && x < 395 && y > 116 && y < 224) continue
    const o = 0.05 + rnd() * 0.11
    out.push({
      x, y,
      r: 0.5 + rnd() * 1.0,
      o,
      layer: out.length % 3,           // a | b | c depth layers for parallax
      tw: rnd() > 0.72,                // a few gently twinkle
      twDelay: (rnd() * 6).toFixed(2),
    })
  }
  return out
})()

const ORDER0 = MODEL.map((_, i) => i)
const shuffle = (arr) => {
  const a = arr.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

// Timings (ms) — slow + calm.
const DWELL = 2800   // per constellation (brighten → draw → resolve)
const GAP = 120      // beat between constellations
const HOLD = 4000    // calm full-sky hold
const DIM = 3000     // fade lines to dim before the rebuild
const SETTLE = 300   // after a fresh cycle mounts

export default function AuthConstellation() {
  const [reduced, setReduced] = useState(false)
  const [order, setOrder] = useState(ORDER0)
  const [forming, setForming] = useState(-1)
  const [done, setDone] = useState([])       // indices settled to ink
  const [dimming, setDimming] = useState(false)
  const [cycle, setCycle] = useState(0)      // remount key → clean reset each loop
  const cancelled = useRef(false)

  useEffect(() => {
    cancelled.current = false
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Reduced motion → render the completed map, fully drawn and still.
    if (prefersReduced) {
      setReduced(true)
      setOrder(ORDER0)
      setForming(-1)
      setDone(ORDER0.slice())
      return () => { cancelled.current = true }
    }

    const timers = []
    const wait = (ms) => new Promise((res) => { timers.push(setTimeout(res, ms)) })

    async function run() {
      let n = 0
      while (!cancelled.current) {
        const ord = n === 0 ? ORDER0 : shuffle(ORDER0)
        n++
        setDimming(false)
        setDone([])
        setForming(-1)
        setOrder(ord)
        setCycle((c) => c + 1)
        await wait(SETTLE)
        if (cancelled.current) return

        for (let k = 0; k < ord.length; k++) {
          setForming(ord[k])
          await wait(DWELL)
          if (cancelled.current) return
          setDone((prev) => (prev.includes(ord[k]) ? prev : [...prev, ord[k]]))
          setForming(-1)
          await wait(GAP)
          if (cancelled.current) return
        }

        await wait(HOLD)              // hold the full sky
        if (cancelled.current) return
        setDimming(true)             // fade lines to dim
        await wait(DIM)
        if (cancelled.current) return
      }
    }

    run()
    return () => { cancelled.current = true; timers.forEach(clearTimeout) }
  }, [])

  const label =
    'ReRight services, connected to your data: ' +
    MODEL.map((c) => `${c.name} — ${c.best}`).join('; ')

  return (
    <div className={`rrcon ${reduced ? 'rrcon--still' : ''}`} role="img" aria-label={label}>
      <svg
        className="rrcon-svg"
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="rrconGlow" cx={ANCHOR.x} cy={ANCHOR.y} r="80" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#ff6a5c" stopOpacity="0.5" />
            <stop offset="45%" stopColor="#e5372b" stopOpacity="0.16" />
            <stop offset="100%" stopColor="#e5372b" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* ── drifting background source-field (3 parallax layers) ─────────── */}
        {[0, 1, 2].map((layer) => (
          <g key={layer} className={`rrcon-bg-${['a', 'b', 'c'][layer]}`}>
            {STARS.filter((s) => s.layer === layer).map((s, i) => (
              <circle
                key={i}
                cx={s.x}
                cy={s.y}
                r={s.r}
                className={`rrcon-star ${s.tw ? 'is-tw' : ''}`}
                style={{ '--o': s.o, opacity: s.o, animationDelay: `${s.twDelay}s` }}
              />
            ))}
          </g>
        ))}

        {/* ── constellations (remounts each cycle for a clean rebuild) ─────── */}
        <g className={`rrcon-field ${dimming ? 'is-dimming' : ''}`} key={cycle}>
          {MODEL.map((c, ci) => {
            const state = forming === ci ? 'is-forming' : (done.includes(ci) ? 'is-done' : '')
            const hubPt = c.pts[c.hub]
            const Icon = c.Icon
            return (
              <g key={ci} className={`rrcon-con ${state}`}>
                {/* asterism lines */}
                {c.edges.map(([a, b], li) => {
                  const p = c.pts[a], q = c.pts[b]
                  if (!p || !q) return null
                  return (
                    <line
                      key={`e${li}`}
                      x1={p.x} y1={p.y} x2={q.x} y2={q.y}
                      pathLength="1"
                      className="rrcon-line"
                      style={{ '--d': `${(0.5 + li * 0.28).toFixed(2)}s` }}
                    />
                  )
                })}
                {/* line up to the anchor mark */}
                {hubPt && (
                  <line
                    x1={hubPt.x} y1={hubPt.y} x2={ANCHOR.x} y2={ANCHOR.y}
                    pathLength="1"
                    className="rrcon-line rrcon-line--anchor"
                    style={{ '--d': `${c.anchorDelay.toFixed(2)}s` }}
                  />
                )}
                {/* points */}
                {c.pts.map((p, pi) => (
                  <circle key={`p${pi}`} cx={p.x} cy={p.y} className="rrcon-pt" />
                ))}
                {/* service badge — icon + one short outcome line */}
                <foreignObject
                  x={c.badge.x} y={c.badge.y} width={c.badge.w} height={c.badge.h}
                  style={{ '--badge-d': `${c.badgeDelay.toFixed(2)}s`, overflow: 'visible' }}
                >
                  <div xmlns="http://www.w3.org/1999/xhtml" className="rrcon-badge">
                    <Icon className="rrcon-badge-ico" size={17} strokeWidth={1.75} />
                    <span className="rrcon-badge-name">{c.name}</span>
                    <span className="rrcon-badge-best">{c.best}</span>
                  </div>
                </foreignObject>
              </g>
            )
          })}
        </g>

        {/* ── the anchor: R | Я mark, brightest + still ────────────────────── */}
        <circle cx={ANCHOR.x} cy={ANCHOR.y} r="80" className="rrcon-anchor-glow" />
        <circle cx={ANCHOR.x} cy={ANCHOR.y} r="52" className="rrcon-anchor-disc" />
        <foreignObject x={ANCHOR.x - 75} y={ANCHOR.y - 42} width="150" height="84">
          <div xmlns="http://www.w3.org/1999/xhtml" className="rrcon-logo">
            <Logo size="md" tone="light" showText={false} />
          </div>
        </foreignObject>
      </svg>
    </div>
  )
}
